import { storesAPI } from "@api/main";
import Cart from "@models/cart";
import { action, computed, flow, observable, runInAction } from "mobx";
import { makeObservable } from "mobx";
import { showToastFromStore } from "@utils/storeToast";
import { analytics } from "../services/analytics";

const initialCartData = {
  can_do_checkout: false,
  cart_session_id: "",
  currency: "",
  global_message: "",
  has_quantity_change: false,
  has_soldout_items: false,
  items: [],
  total_items: 0,
  total_items_value: 0,
  total_items_value_string: "",
  total_unique_items: 0,
};

export default class CartStore {
  cart: Cart = new Cart(initialCartData);

  loading: boolean = false;

  private _fetchCartPromise: Promise<Cart> | null = null;

  constructor() {
    makeObservable(this, {
      cart: observable,
      loading: observable,
      items: computed,
      clearCart: action,
      fetchCart: flow,
      addItemToCart: flow,
      removeItemFromCart: flow,
    });
  }

  get items() {
    return this.cart.items;
  }

  clearCart() {
    this.cart = new Cart(initialCartData);
  }

  *fetchCart() {
    if (!this._fetchCartPromise) this._fetchCartPromise = storesAPI.getCart();
    const promise = this._fetchCartPromise;
    try {
      const cart: Cart = yield promise;
      runInAction(() => {
        this.cart = cart;
      });
    } finally {
      if (this._fetchCartPromise === promise) this._fetchCartPromise = null;
    }
  }

  *addItemToCart(productId: string): any {
    runInAction(() => {
      this.loading = true;
    });
    try {
      const result = yield storesAPI.addToCart(productId);
      if (result.message.trim()) {
        showToastFromStore(
          result.message,
          result.ok ? "success" : "warning",
          5000,
          result.ok ? { variant: "cart" } : undefined
        );
      }
      if (result.ok) {
        yield this.fetchCart();
        return result.response;
      }
      return null;
    } catch (error: any) {
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  *removeItemFromCart(productId: string, quantity?: number) {
    runInAction(() => {
      this.loading = true;
    });
    // Snapshot before the API mutates the cart: only a FULL removal counts
    // as cart_item_removed — a quantity decrement (no/partial quantity arg
    // while more units remain) is silent, symmetric with untracked
    // quantity increments.
    const existing = this.cart.items.find(
      (item) => String(item.id) === String(productId)
    );
    const removesAllUnits =
      quantity === undefined
        ? (existing?.quantity ?? 1) <= 1
        : quantity >= (existing?.quantity ?? quantity);
    try {
      yield storesAPI.removeFromCart(productId, quantity);
      // Store-level choke point — no component tracks removals. The cart
      // page's "remove all" passes the sentinel "all".
      if (productId === "all") {
        analytics.track("cart_cleared", {});
      } else if (removesAllUnits) {
        analytics.track("cart_item_removed", {
          product_id: productId,
          product_title: existing?.title,
        });
      }
      yield this.fetchCart();
    } catch (error: any) {
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }
}
